import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useApiSend, getApiErrorMessage } from '../../utils/httpClientTs';
import {
  createFundUndesignatedAllocation,
  patchFundUndesignatedAllocation,
  deleteFundUndesignatedAllocation,
} from '../../endpoints/financeFunds';
import type { FundUndesignatedAllocationDTO } from '../../types/financeFunds';
import AlertItem from '../../Components/AlertItem';

interface FundUndesignatedAllocationsSectionProps {
  fundId: string;
  allocations: FundUndesignatedAllocationDTO[];
  canManage: boolean;
}

type FormState = {
  year: string;
  amount: string;
};

const emptyForm = (): FormState => ({
  year: '',
  amount: '',
});

const formatAmount = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FundUndesignatedAllocationsSection: React.FC<
  FundUndesignatedAllocationsSectionProps
> = ({ fundId, allocations, canManage }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const invalidateKeys = [
    ['fund-detail', fundId],
    ['fund-history', fundId],
  ];

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setActionError(null);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setActionError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: FundUndesignatedAllocationDTO) => {
    setEditingId(row.id);
    setForm({
      year: String(row.year),
      amount: String(row.amount),
    });
    setActionError(null);
    setDialogOpen(true);
  };

  const validateForm = (): string | null => {
    const amount = Number(form.amount);
    const year = Number(form.year);
    if (!Number.isInteger(year) || year < 1900) {
      return 'Year is required';
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'Amount must be greater than zero';
    }
    return null;
  };

  const buildPayload = () => ({
    year: Number(form.year),
    amount: Number(form.amount),
  });

  const { mutateAsync: createMut, isPending: creating } = useApiSend(
    (body: ReturnType<typeof buildPayload>) =>
      createFundUndesignatedAllocation(fundId, body),
    closeDialog,
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutateAsync: patchMut, isPending: patching } = useApiSend(
    (p: { id: string; body: ReturnType<typeof buildPayload> }) =>
      patchFundUndesignatedAllocation(fundId, p.id, p.body),
    closeDialog,
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutateAsync: deleteMut, isPending: deleting } = useApiSend(
    (id: string) => deleteFundUndesignatedAllocation(fundId, id),
    () => setDeleteId(null),
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      setActionError(err);
      return;
    }
    const body = buildPayload();
    if (editingId) {
      await patchMut({ id: editingId, body });
    } else {
      await createMut(body);
    }
  };

  return (
    <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
      <CardHeader
        title="Undesignated allocations"
        action={
          canManage ? (
            <Button startIcon={<Add />} onClick={openCreate} size="small">
              Add
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {actionError && !dialogOpen && (
          <Box sx={{ mb: 2 }}>
            <AlertItem
              severity="error"
              description={actionError}
              onClose={() => setActionError(null)}
              keepOpen
            />
          </Box>
        )}
        {allocations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No undesignated allocations defined for this primary fund.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell align="right">Amount</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.year}</TableCell>
                  <TableCell align="right">
                    ${formatAmount(row.amount)}
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="Edit undesignated allocation"
                        onClick={() => openEdit(row)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete undesignated allocation"
                        onClick={() => setDeleteId(row.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId
            ? 'Edit undesignated allocation'
            : 'Add undesignated allocation'}
        </DialogTitle>
        <DialogContent>
          {actionError && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <AlertItem
                severity="error"
                description={actionError}
                onClose={() => setActionError(null)}
                keepOpen
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Year"
              type="number"
              size="small"
              fullWidth
              required
              inputProps={{ min: 1900, step: 1 }}
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
            />
            <TextField
              label="Amount"
              type="number"
              size="small"
              fullWidth
              required
              inputProps={{ min: 0.01, step: '0.01' }}
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={creating || patching}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete undesignated allocation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => {
              if (deleteId) void deleteMut(deleteId);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FundUndesignatedAllocationsSection;
